import { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';

/**
 * ShareBar — reusable social-sharing control for public content.
 *
 * Renders round icon buttons for Facebook, X, WhatsApp, Viber and a Copy-link
 * action (Clipboard API with a select+copy fallback). A native "Share" button is
 * additionally shown only when `navigator.share` is available (mobile).
 *
 * The shared URL is derived from the live location (origin + pathname) so it is
 * always the canonical SPA URL — never a hardcoded host. Pass `url` to override.
 *
 * Props:
 *   - title: string  — content title, used as share text.
 *   - url?:  string  — override the shared URL (defaults to the current page URL).
 *   - className?: string — extra classes on the wrapper.
 */
export default function ShareBar({ title = '', url, className = '' }) {
  const toast = useToast();
  const [canNativeShare, setCanNativeShare] = useState(false);

  // navigator.share is client-only; resolve after mount to avoid SSR/hydration drift.
  useEffect(() => {
    setCanNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const shareUrl =
    url ||
    (typeof window !== 'undefined'
      ? window.location.origin + window.location.pathname
      : '');

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedTitleUrl = encodeURIComponent(title ? `${title} ${shareUrl}` : shareUrl);

  const targets = [
    {
      key: 'facebook',
      label: 'Share on Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: IconFacebook,
    },
    {
      key: 'x',
      label: 'Share on X',
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: IconX,
    },
    {
      key: 'whatsapp',
      label: 'Share on WhatsApp',
      href: `https://wa.me/?text=${encodedTitleUrl}`,
      icon: IconWhatsApp,
    },
    {
      key: 'viber',
      label: 'Share on Viber',
      href: `viber://forward?text=${encodedTitleUrl}`,
      icon: IconViber,
    },
  ];

  async function copyLink() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        legacyCopy(shareUrl);
      }
      toast.success('Link copied to clipboard');
    } catch {
      // Clipboard API can reject (permissions/insecure context) — try the fallback.
      if (legacyCopy(shareUrl)) {
        toast.success('Link copied to clipboard');
      } else {
        toast.error('Could not copy the link');
      }
    }
  }

  async function nativeShare() {
    try {
      await navigator.share({ title, url: shareUrl });
    } catch {
      // User cancelled or share failed — no toast needed on cancel.
    }
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      role="group"
      aria-label="Share this page"
    >
      <span className="mr-1 text-xs font-semibold uppercase tracking-[0.15em] text-ink-muted">
        Share
      </span>

      {targets.map(({ key, label, href, icon: Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className={btnCls}
        >
          <Icon />
        </a>
      ))}

      <button type="button" onClick={copyLink} aria-label="Copy link" title="Copy link" className={btnCls}>
        <IconLink />
      </button>

      {canNativeShare && (
        <button
          type="button"
          onClick={nativeShare}
          aria-label="Share via device"
          title="Share via device"
          className={btnCls}
        >
          <IconShare />
        </button>
      )}
    </div>
  );
}

const btnCls =
  'inline-flex h-10 w-10 items-center justify-center rounded-full bg-maroon-50 text-maroon-700 ' +
  'transition-colors duration-200 hover:bg-maroon-700 hover:text-white ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 ' +
  'motion-safe:hover:-translate-y-0.5 motion-safe:transition-transform';

function legacyCopy(text) {
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

/* ---- Icons (currentColor, 18px) ------------------------------------------ */

const svgProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': 'true',
  focusable: 'false',
};

function IconFacebook() {
  return (
    <svg {...svgProps}>
      <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H17V3.6c-.3-.04-1.3-.13-2.46-.13-2.43 0-4.1 1.48-4.1 4.21V9.9H7.7V13h2.74v8h3.06z" />
    </svg>
  );
}

function IconX() {
  return (
    <svg {...svgProps}>
      <path d="M17.53 3H20.5l-6.49 7.42L21.75 21h-5.98l-4.68-6.12L5.7 21H2.72l6.94-7.93L2.25 3h6.13l4.23 5.6L17.53 3zm-1.05 16.2h1.65L7.6 4.71H5.83l10.65 14.49z" />
    </svg>
  );
}

function IconWhatsApp() {
  return (
    <svg {...svgProps}>
      <path d="M12.04 2a9.9 9.9 0 00-8.46 15l-1.06 3.86 3.96-1.04A9.9 9.9 0 1012.04 2zm0 1.8a8.1 8.1 0 016.8 12.48l-.19.3.62 2.26-2.32-.61-.29.17A8.1 8.1 0 1112.04 3.8zm-3.1 4.1c-.16 0-.42.06-.64.3-.22.24-.85.83-.85 2.02s.87 2.34 1 2.5c.12.16 1.71 2.72 4.23 3.7 2.09.82 2.52.66 2.98.62.46-.04 1.48-.6 1.69-1.19.2-.58.2-1.08.14-1.18-.06-.1-.22-.16-.46-.28-.24-.12-1.48-.73-1.7-.81-.23-.08-.4-.12-.56.12-.16.24-.64.8-.79.97-.14.16-.29.18-.53.06-.24-.12-1.03-.38-1.96-1.21-.72-.64-1.21-1.44-1.35-1.68-.14-.24-.02-.37.1-.49.11-.11.24-.29.36-.43.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.32-.76-1.8-.18-.42-.37-.42-.53-.43h-.46z" />
    </svg>
  );
}

function IconViber() {
  return (
    <svg {...svgProps}>
      <path d="M12 2C7.9 2 4.5 4.7 4.5 8.1c0 1.7.9 3.3 2.3 4.5v3.1l2.5-1.4c.8.2 1.7.3 2.7.3 4.1 0 7.5-2.7 7.5-6.1S16.1 2 12 2zm.02 1.6c3.3 0 5.9 2.05 5.9 4.5s-2.6 4.5-5.9 4.5c-.86 0-1.7-.1-2.44-.32l-.3-.09-1.4.78v-1.7l-.27-.2c-1.2-.9-1.9-2.05-1.9-3.25 0-2.45 2.6-4.5 5.9-4.5zm-2.62 2.2c-.13 0-.32.02-.48.19-.16.17-.6.55-.6 1.34 0 .8.61 1.56.69 1.67.09.11 1.2 1.9 2.97 2.53 1.47.52 1.77.42 2.09.39.32-.03 1.03-.42 1.18-.82.14-.4.14-.75.1-.82-.04-.07-.16-.11-.32-.19-.17-.08-1.03-.5-1.19-.56-.16-.06-.28-.09-.4.09-.11.17-.45.56-.55.68-.1.11-.2.13-.37.04-.16-.08-.72-.26-1.36-.85-.5-.45-.84-1-.94-1.17-.1-.17-.01-.26.07-.34.08-.08.17-.2.25-.31.09-.11.11-.19.17-.31.05-.11.03-.21-.01-.29-.05-.08-.4-.94-.53-1.28-.13-.32-.26-.29-.36-.3h-.31z" />
    </svg>
  );
}

function IconLink() {
  return (
    <svg {...svgProps}>
      <path d="M10.6 13.4a1 1 0 001.4 0l4-4a2.5 2.5 0 00-3.5-3.5l-1.3 1.2a1 1 0 001.4 1.4l1.3-1.2a.5.5 0 01.7.7l-4 4a1 1 0 000 1.4zm2.8-2.8a1 1 0 00-1.4 0l-4 4a2.5 2.5 0 003.5 3.5l1.3-1.2a1 1 0 00-1.4-1.4l-1.3 1.2a.5.5 0 01-.7-.7l4-4a1 1 0 000-1.4z" />
    </svg>
  );
}

function IconShare() {
  return (
    <svg {...svgProps}>
      <path d="M18 16.1c-.76 0-1.44.3-1.96.77L8.9 12.7c.05-.23.05-.47 0-.7l7.05-4.11c.54.5 1.25.81 2.05.81a3 3 0 100-6 3 3 0 00-3 3c0 .24.03.47.09.7L8.04 10.5A3 3 0 003 12.35a3 3 0 005.04 2.19l7.12 4.16c-.06.21-.09.43-.09.65a2.85 2.85 0 105.93-.05A3 3 0 0018 16.1z" />
    </svg>
  );
}
